import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  CircularProgressProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface IDataShowerProps extends BoxProps {
  loading?: boolean;
  loadingProps?: CircularProgressProps;
  loadingContainerProps?: BoxProps;
  success?: boolean;
  isEmpty?: boolean;
  EmptyContentComponent?: ReactNode;
  SkeletonComponents?: ReactNode;
  repeatBtnText?: string;
  repeatAction?: () => void;
  errorMessage?: string;
  fillContent?: boolean;
  keepChildrenMountOnLoading?: boolean;
}

/**
 * Компонент для отображение контента получаемый из апи с лоадером и ошибкой с кнопкой повторить.
 *
 * @param success если `true`, отображается контент, иначе ошибка.
 * @param loading отображение лоадера
 * @param loadingProps extends от CircularProgressProps
 * @param loadingContainerProps extends от BoxProps
 * @param isEmpty отображать заглушку
 * @param EmptyContentComponent компонент заглушки
 * @param SkeletonComponents компоненты skeleton вместо лоадера
 * @param repeatBtnText кастомный текст кнопки повтора в случае ошибки. По умолчанию `"Повторить"`.
 * @param repeatAction функция для повтора запроса, запускается по кнопке "Повторить".
 * @param errorMessage кастомная ошибка.
 * @param fillContent заполнять всю пустую область родительского контента. Обычно используется чтобы сделать лоадер по центру.
 * @param keepChildrenMountOnLoading контент остается отрендеренным, а лоадер остается поверх контента с белым фоном. Может пригодиться если нужно контент оставлять отрендеренным. По умолчанию, контент отображается, если `loader=false && success && true`;
 */
export const DataShower = React.forwardRef<HTMLDivElement, IDataShowerProps>((props, ref) => {
  const { t } = useTranslation('common');

  const {
    success = true,
    loading,
    loadingProps,
    loadingContainerProps,
    isEmpty,
    EmptyContentComponent,
    SkeletonComponents,
    repeatBtnText: updateBtnText = t('button.repeat'),
    repeatAction,
    errorMessage,
    fillContent,
    keepChildrenMountOnLoading,
    children,
    className,
    ...rest
  } = props;

  const theme = useTheme();
  const { classes, cx } = useStyles({ theme });

  // Renders

  const renderLoader = (isAbsolute?: boolean) => {
    return (
      <Box
        {...loadingContainerProps}
        className={cx(
          isAbsolute ? classes.loadingAbsoluteContainer : classes.loadingContainer,
          loadingContainerProps?.className,
        )}
      >
        {SkeletonComponents ? SkeletonComponents : <CircularProgress {...loadingProps} />}
      </Box>
    );
  };

  const renderContent = () => {
    if (loading && !keepChildrenMountOnLoading) {
      return renderLoader();
    }

    if (!success) {
      return (
        <div className={classes.errorContainer}>
          <Box mb={1}>
            <Typography>{errorMessage || t('error.somethingWrong')}</Typography>
          </Box>

          {repeatAction && (
            <Button color="secondary" onClick={repeatAction}>
              {updateBtnText}
            </Button>
          )}
        </div>
      );
    }

    return (
      <>
        {loading && keepChildrenMountOnLoading && renderLoader(true)}
        {isEmpty ? EmptyContentComponent : children}
      </>
    );
  };

  return (
    <Box ref={ref} minHeight={32} {...rest} className={cx(classes.dataShowerContainer, { fillContent }, className)}>
      {renderContent()}
    </Box>
  );
});

const useStyles = makeStyles<{ theme: Theme }>()((_, { theme }) => ({
  dataShowerContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    '&.fillContent': {
      flex: 1,
      height: '100%',
    },
  },
  loadingContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingAbsoluteContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
    zIndex: 999,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
}));
